import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Loader2, Shield, Sparkles, Disc3, Bookmark, MessageSquare, ExternalLink } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const restore = (navigate, payload) => {
  sessionStorage.setItem("pg_restore", JSON.stringify(payload));
  navigate("/");
};

const RestoreCard = ({ testId, isChain, query, plugins, onClick }) => {
  const names = (plugins || []).slice(0, 3).map((p) => p.name).join(" · ");
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className="block w-full rounded-md border border-cyan-500/25 bg-cyan-500/5 px-4 py-3 text-left transition-colors duration-200 hover:border-cyan-500/50"
    >
      <div className="flex items-center gap-2">
        {isChain ? <Disc3 className="h-3.5 w-3.5 text-cyan-400" /> : <Sparkles className="h-3.5 w-3.5 text-cyan-400" />}
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-400">
          {isChain ? "Chain" : "Search"}
        </span>
        <span className="ml-auto flex items-center gap-1 text-[11px] font-semibold text-cyan-300">
          Open <ExternalLink className="h-3 w-3" />
        </span>
      </div>
      <div className="mt-1.5 font-heading text-sm font-bold text-white">"{query}"</div>
      <div className="mt-1 text-xs text-white/50">
        {plugins?.length || 0} plugins{names ? ` — ${names}${plugins.length > 3 ? "..." : ""}` : ""}
      </div>
    </button>
  );
};

export const ProfileModal = ({ username, onClose }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!username) { setProfile(null); return; }
    setLoading(true);
    setProfile(null);
    fetch(`${API}/api/users/${encodeURIComponent(username)}`)
      .then((r) => { if (!r.ok) throw new Error("Not found"); return r.json(); })
      .then(setProfile)
      .catch(() => setProfile({ error: true }))
      .finally(() => setLoading(false));
  }, [username]);

  const joined = profile?.created_at ? new Date(profile.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short" }) : null;

  return (
    <Dialog open={!!username} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="profile-modal" className="max-h-[85vh] max-w-lg overflow-y-auto border-white/10 bg-[#111114] text-white">
        {loading && (
          <div className="flex items-center justify-center py-16">
            <DialogTitle className="sr-only">Loading profile</DialogTitle>
            <DialogDescription className="sr-only">Loading member profile</DialogDescription>
            <Loader2 className="h-6 w-6 animate-spin text-cyan-400" />
          </div>
        )}
        {profile?.error && (
          <div className="py-10 text-center">
            <DialogTitle className="font-heading text-lg font-bold">Member not found</DialogTitle>
            <DialogDescription className="mt-1 text-sm text-white/50">This user no longer exists.</DialogDescription>
          </div>
        )}
        {profile && !profile.error && (
          <div>
            <div className="flex items-center gap-4">
              <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full font-heading text-2xl font-black text-black"
                    style={{ background: profile.avatar_color || "#00F0FF" }}>
                {(profile.username || "?").charAt(0).toUpperCase()}
              </span>
              <div>
                <DialogTitle data-testid="profile-username" className="font-heading text-2xl font-black tracking-tight">
                  {profile.username}
                </DialogTitle>
                <DialogDescription className="mt-1 flex items-center gap-2 text-sm text-white/50">
                  {profile.role === "admin" && (
                    <span data-testid="profile-role-badge" className="flex items-center gap-1 rounded bg-cyan-500/15 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest text-cyan-300">
                      <Shield className="h-3 w-3" /> Admin
                    </span>
                  )}
                  {joined && <span>Joined {joined}</span>}
                </DialogDescription>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-3 gap-3">
              {[
                ["Messages", profile.message_count, MessageSquare],
                ["Shared", profile.shares?.length || 0, Sparkles],
                ["Library", profile.library?.length || 0, Bookmark],
              ].map(([label, val, Icon]) => (
                <div key={label} className="rounded-md border border-white/10 bg-black/30 p-3 text-center">
                  <Icon className="mx-auto h-4 w-4 text-cyan-400" />
                  <div className="mt-1.5 font-heading text-xl font-black">{val}</div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/40">{label}</div>
                </div>
              ))}
            </div>

            {profile.shares?.length > 0 && (
              <div className="mt-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">Shared to community</div>
                <div className="mt-2 space-y-2">
                  {profile.shares.map((s) => (
                    <RestoreCard
                      key={s.id}
                      testId="profile-share-item"
                      isChain={s.share_ref.mode === "match"}
                      query={s.share_ref.query}
                      plugins={s.share_ref.plugins}
                      onClick={() => restore(navigate, {
                        query: s.share_ref.query, mode: s.share_ref.mode, intent: s.share_ref.intent,
                        analysis: s.share_ref.analysis, results: s.share_ref.plugins,
                      })}
                    />
                  ))}
                </div>
              </div>
            )}

            {profile.library?.length > 0 && (
              <div className="mt-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">Saved library</div>
                <div className="mt-2 space-y-2">
                  {profile.library.map((l) => (
                    <RestoreCard
                      key={l.id}
                      testId="profile-library-item"
                      isChain={l.kind === "chain"}
                      query={l.query}
                      plugins={l.results}
                      onClick={() => restore(navigate, {
                        query: l.query, mode: l.mode, intent: l.intent,
                        analysis: l.analysis, results: l.results,
                      })}
                    />
                  ))}
                </div>
              </div>
            )}

            {!profile.shares?.length && !profile.library?.length && (
              <p className="mt-6 text-center font-mono text-xs uppercase tracking-widest text-white/25">
                No shared picks yet
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
